import { useWorkspaceText } from '../interface-copy';
import { useEffect, useId, useState } from 'react';
import { ConsentCard } from './consent-card';
import { CodingAgentList } from './coding-agent-list';
import { codingAgentsSource } from './coding-agents-source';
import { useCodingAgentsCopy } from './coding-agents-copy';
import type { CodingAgentsVm, CodingLoaded, ConsentRequest } from './coding-agents-types';

type State = { status: 'loading' } | CodingLoaded<CodingAgentsVm>;

/** Connections → Coding agents. Hidden until the server side is registered. */
export function CodingAgentsSection() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const {
    CODING_AGENTS_TITLE,
    CODING_AGENTS_INTRO,
    CODING_AGENTS_LOADING,
    CODING_AGENTS_ERROR,
    CODING_AGENTS_NONE,
    YOUR_AGENTS,
    COMPANY_AGENTS_HELP,
    COMPANY_AGENTS,
  } = useCodingAgentsCopy();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [request, setRequest] = useState<CodingLoaded<ConsentRequest> | null>(null);
  const heading = useId();

  const load = async () => {
    try {
      setState(await codingAgentsSource().list());
    } catch {
      setState({ status: 'error' });
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const review = async (requestId: string) => {
    try {
      setRequest(await codingAgentsSource().loadRequest(requestId));
    } catch {
      setRequest({ status: 'error' });
    }
  };

  if (state.status === 'unavailable') return null;
  const vm = state.status === 'ready' ? state.value : null;
  const none = vm && vm.mine.length === 0 && (vm.company?.length ?? 0) === 0;

  return (
    <section
      className="ws-connection-scope ws-agent-section ws-coding-agents"
      aria-labelledby={heading}
    >
      <div className="ws-connection-section-label">
        <h2 id={heading}>{CODING_AGENTS_TITLE}</h2>
      </div>
      <p className="ws-connection-audience">{CODING_AGENTS_INTRO}</p>
      {state.status === 'loading' && <p role="status">{CODING_AGENTS_LOADING}</p>}
      {state.status === 'error' && (
        <div>
          <p role="alert">{CODING_AGENTS_ERROR}</p>
          <button type="button" className="ws-text-button" onClick={() => void load()}>
            {uiText('Try again')}
          </button>
        </div>
      )}
      {request?.status === 'ready' && (
        <ConsentCard
          request={request.value}
          onDecided={(result) => {
            if (result === 'done') setRequest(null);
            void load();
          }}
        />
      )}
      {request && request.status !== 'ready' && <p role="alert">{CODING_AGENTS_ERROR}</p>}
      {none && <p>{CODING_AGENTS_NONE}</p>}
      {vm && vm.mine.length > 0 && (
        <CodingAgentList
          grants={vm.mine}
          label={YOUR_AGENTS}
          showOwner={false}
          onReview={(id) => void review(id)}
          onChanged={() => void load()}
        />
      )}
      {vm?.canAdminister && vm.company && vm.company.length > 0 && (
        <>
          <p className="ws-connection-audience">{COMPANY_AGENTS_HELP}</p>
          <CodingAgentList
            grants={vm.company}
            label={COMPANY_AGENTS}
            showOwner
            onReview={(id) => void review(id)}
            onChanged={() => void load()}
          />
        </>
      )}
    </section>
  );
}
